import { Currency, Multiplier } from '@prisma/client'
import { DEFAULT_CURRENCY, convertCurrency } from '@/lib/services/currency/conversion'
import type { ServiceItem } from '@/lib/types/operations/service'

interface CalculatePriceWithMultipliersProps {
  service: ServiceItem
  multipliers: Multiplier[]
  vehicleDefinitionId?: string
  shopLocationId?: string
  vehicleType?: string
  userCurrency?: Currency
}

/**
 * Multiplier-based price calculation for the appointment/payment/snapshot flows.
 * Uses the Prisma Multiplier rows (location, vehicle-definition, vehicle-type, service/category)
 * to compute: basePrice × locationMultiplier × vehicleMultiplier × serviceMultiplier.
 *
 * The new wrap-install estimator (estimateDetailed) uses a different model
 * (surface-area × material cost + labour hours × rate). This function is the
 * correct pricing path for existing appointment and payment flows.
 */
export function calculatePriceWithMultipliers({
  service,
  multipliers,
  vehicleDefinitionId,
  shopLocationId,
  vehicleType,
  userCurrency = DEFAULT_CURRENCY
}: CalculatePriceWithMultipliersProps): number {
  const basePrice = service.price || 0
  if (basePrice === 0) return 0

  let locationMultiplier = 1.0
  let vehicleMultiplier = 1.0
  let serviceMultiplier = 1.0

  multipliers.forEach(multiplier => {
    const matchesService = multiplier.serviceId === service.id
    const matchesCategory = multiplier.serviceCategoryId === service.primaryCategoryId
    const matchesVehicleDefinition = multiplier.vehicleDefinitionId === vehicleDefinitionId
    const matchesLocation = multiplier.shopLocationId === shopLocationId
    const matchesVehicleType = multiplier.vehicleType === vehicleType
    const isGeneralMultiplier = !multiplier.serviceId && !multiplier.serviceCategoryId

    if (!matchesService && !matchesCategory && !isGeneralMultiplier) return

    if (matchesLocation && shopLocationId) {
      locationMultiplier *= multiplier.value
    } else if (matchesVehicleDefinition && vehicleDefinitionId) {
      vehicleMultiplier *= multiplier.value
    } else if (matchesVehicleType && vehicleType && !vehicleDefinitionId) {
      vehicleMultiplier *= multiplier.value
    } else if (matchesService && !matchesLocation && !matchesVehicleDefinition && !matchesVehicleType) {
      serviceMultiplier *= multiplier.value
    } else if (matchesCategory && !matchesService && !matchesLocation && !matchesVehicleDefinition && !matchesVehicleType) {
      serviceMultiplier *= multiplier.value
    }
  })

  const finalPriceCAD = basePrice * locationMultiplier * vehicleMultiplier * serviceMultiplier
  const finalPrice = convertCurrency(finalPriceCAD, DEFAULT_CURRENCY, userCurrency)
  return Math.round(finalPrice * 100) / 100
}

export function formatPrice(price: number, currency: Currency = DEFAULT_CURRENCY, locale: string = 'en-CA'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price)
}

export function calculateTotalWithTax(subtotal: number, taxRate: number): {
  subtotal: number
  tax: number
  total: number
} {
  const tax = subtotal * taxRate
  const total = subtotal + tax

  return {
    subtotal,
    tax,
    total
  }
}
